import { type LDFlagSet } from "launchdarkly-react-client-sdk";
import React, { useMemo, useRef } from "react";

import { format } from "date-fns";
import { isEqual } from "lodash";

import PdfDocumentFooterReportV3 from "components/pdf-generation-v3/footer-v3/pdf-document-footer-report-v3.component";
import PdfDocumentHeaderReport from "components/pdf-generation-v3/header/pdf-document-header-report";
import PdfDocument from "components/pdf-generation-v3/pdf-document.component";
import { type Clinic } from "services/types/clinics.type";
import type { Patient } from "services/types/patients.type";
import type { MinimalStaffMember } from "services/types/staff.type";

export type CustomPrintFormPdfRenderProps = {
  clinic?: Clinic;
  content: string;
  staffMember?: MinimalStaffMember;
  staffMemberSignature?: string;
  patient?: Patient;
  fileName: string;
  enableHeader: boolean;
  enablePatientSignature: boolean;
  enableProviderSignature: boolean;
  signature?: File | Blob;
  signedAt?: string | null;
  dateOfVisit?: string | null;
  flags: LDFlagSet;
  isPdfNativeTablesEnabled?: boolean;
};

const DATE_MASK = "MM/dd/yyyy";

export const CustomPrintFormPdfRender: React.FC<
  CustomPrintFormPdfRenderProps
> = ({
  clinic,
  content,
  staffMember,
  patient,
  fileName,
  enableHeader,
  enablePatientSignature,
  enableProviderSignature,
  signature,
  staffMemberSignature,
  signedAt,
  dateOfVisit,
  isPdfNativeTablesEnabled
}) => {
  const signatureUrlRef = useRef<string | undefined>();
  const footer = useMemo(() => {
    const signedAtDate = signedAt
      ? format(new Date(signedAt), DATE_MASK)
      : undefined;

    const signatures = [];

    if (enableProviderSignature) {
      signatures.push({
        label: "Provider Signature",
        staffMember: {
          ...staffMember,
          signature: staffMemberSignature
        },
        licenseNumber: staffMember?.license_number ?? undefined
      });
    }

    if (enablePatientSignature) {
      if (signatureUrlRef.current) {
        URL.revokeObjectURL(signatureUrlRef.current);
      }
      if (signature) {
        signatureUrlRef.current = URL.createObjectURL(signature);
      }
      signatures.push({
        label: "Patient Signature",
        staffMember: {
          first_name: String(patient?.first_name),
          last_name: String(patient?.last_name),
          title: patient?.title ?? undefined,
          suffix: patient?.suffix,
          signature: signature ? URL.createObjectURL(signature) : undefined
        }
      });
    }
    if (!signatures.length) {
      return null;
    }
    return (
      <PdfDocumentFooterReportV3
        signedAtDate={signedAtDate}
        signatures={signatures}
      />
    );
  }, [
    enablePatientSignature,
    enableProviderSignature,
    patient?.first_name,
    patient?.last_name,
    patient?.suffix,
    patient?.title,
    staffMember,
    signature,
    signedAt,
    staffMemberSignature
  ]);

  if (!patient) return null;

  const dateOfVisitFormatted = dateOfVisit
    ? format(new Date(dateOfVisit), DATE_MASK)
    : "";

  return (
    <PdfDocument
      title={fileName}
      pages={[
        {
          bodyHtml: `${content}`,
          pdfNativeTablesEnabled: isPdfNativeTablesEnabled,
          header: enableHeader ? (
            <PdfDocumentHeaderReport
              clinic={clinic}
              dateOfVisit={dateOfVisitFormatted}
              patient={patient}
              staffMember={staffMember}
            />
          ) : undefined,

          footer
        }
      ]}
    />
  );
};

const MemoizedCustomPrintFormPdfRender = React.memo(
  CustomPrintFormPdfRender,
  (prevProps, nextProps) => {
    return isEqual(prevProps, nextProps);
  }
);

export default MemoizedCustomPrintFormPdfRender;
